/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import {
  Modal,
  FileUploaderItem,
  FileUploaderDropContainer
} from 'carbon-components-react';
import cx from 'classnames';
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { useFormHelpers } from '../../../hooks/useFormHelpers';
import { sharedMessages } from '../../messages';
import { FIELDS } from '../schema';
import { parsePhoneCSV } from './helpers';
import { PhoneImportModalMessages as messages } from './messages';

import './PhoneImportModal.scss';

interface PhoneImportModalProps {
  /**
   *
   */
  openImportModal: boolean;
  /**
   *
   */
  handleImportSave: (importedArray: any) => void;
  /**
   *
   */
  handleImportClose: () => void;
  /**
   *
   */
  setImporting: (importing: boolean) => void;
  /**
   *
   */
  importing: boolean;
}
export const PhoneImportModal = ({
  openImportModal,
  handleImportSave,
  handleImportClose,
  setImporting,
  importing
}: PhoneImportModalProps) => {
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { getFieldValue } = useFormHelpers(formMethods);
  const watchedFields = getFieldValue(FIELDS.PHONE_NUMBERS);

  const [importFileName, setImportFileName] = useState<string>('');
  const [importedArray, setImportedArray] = useState<any[]>([]);
  const [importDuplicateCount, setImportDuplicateCount] = useState<number>(0);
  const [importError, setImportError] = useState<string>('');

  const handleFileRemoved = () => {
    setImportFileName('');
    setImportError('');
    setImportedArray([]);
    setImportDuplicateCount(0);
  };

  const removeDuplicates = importedArray => {
    const retVal = [];
    let dupCnt = 0;
    // if the phone value already exists in phoneNumbers, or is a repeated value
    // in the imported file (found in retVal), drop it. count drops.
    for (let i = 0; i < importedArray.length; i++) {
      const candidate = importedArray[i];
      let dupFound = watchedFields?.find(
        el => el.phone_number === candidate.phone_number
      );
      if (dupFound) {
        dupCnt++;
      } else {
        dupFound = retVal.find(
          el => el.phone_number === candidate.phone_number
        );
        if (dupFound) {
          dupCnt++;
        } else {
          retVal.push(candidate);
        }
      }
    }
    setImportDuplicateCount(dupCnt);
    return retVal;
  };

  const handleFileAdded = async (evt, { addedFiles }) => {
    if (addedFiles && addedFiles.length) {
      const [file] = addedFiles;
      setImportFileName(file.name);
      setImporting(true);
      try {
        const fileReader = new FileReader();
        const text = await new Promise((resolve, reject) => {
          fileReader.onload = e => resolve(e.target.result);
          fileReader.onerror = e => reject(e);
          fileReader.readAsText(file);
        });
        const rawImport = parsePhoneCSV(text, watchedFields?.length || 0);
        const importedArray = removeDuplicates(rawImport);
        setImportedArray(importedArray);
        setImporting(false);
        setImportError('');
      } catch (e) {
        setImportedArray([]);
        setImportDuplicateCount(0);
        setImporting(false);
        setImportError(e.message);
      }
    }
  };

  const getResultsSummary = () => {
    // style bold red if no successful numbers, or if nonzero duplicates found
    return (
      <div>
        <span
          className={cx({ 'PMImport--notice': importedArray.length === 0 })}>
          {t(messages.importResults, {
            num: importedArray.length
          })}
        </span>
        <br />
        <span
          className={cx({ 'PMImport--notice': importDuplicateCount !== 0 })}>
          {t(messages.importDuplicates, {
            num: importDuplicateCount
          })}
        </span>
      </div>
    );
  };

  return (
    <>
      {ReactDOM.createPortal(
        <Modal
          modalHeading={t(messages.upload)}
          open={openImportModal}
          primaryButtonText={t(messages.apply)}
          primaryButtonDisabled={importedArray.length === 0}
          secondaryButtonText={t(sharedMessages.cancel)}
          onRequestSubmit={() => handleImportSave(importedArray)}
          onRequestClose={() => handleImportClose()}>
          <div className="PhoneModal__content">
            <div className="PhoneModal__instruction">
              <div>{t(messages.importInstructOne)}</div>
              <div>{t(messages.importInstructTwo)}</div>
            </div>
            <FileUploaderDropContainer
              id="PhoneModal__drop"
              accept={['.csv']}
              labelText={t(messages.uploadLabelChoose)}
              onAddFiles={handleFileAdded}
              disabled={importFileName !== ''}
              data-modal-primary-focus
            />
            {importFileName && (
              <div className="PhoneModal__file">
                <FileUploaderItem
                  name={importFileName}
                  status={importing ? 'uploading' : 'edit'}
                  onDelete={handleFileRemoved}
                  iconDescription={t(messages.importDelete)}
                  invalid={importError !== ''}
                />
                {!importing && !importError && <div>{getResultsSummary()}</div>}
                {!importing && importError !== '' && (
                  <div className="PhoneModal__error">
                    {t(messages.importError)}
                    <br />
                    {importError}
                  </div>
                )}
              </div>
            )}
          </div>
        </Modal>,
        document.body
      )}
    </>
  );
};
